import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map, mergeMap, shareReplay, startWith,
    switchMap, tap,
    withLatestFrom
} from 'rxjs/operators';
import { Cart, CartGroup, CartItem } from '@app/model/cart.model';
import { VoucherItem, VoucherItemAdjustment, VoucherQualifier } from '@app/cart/services/voucher-qualifiers/data';
import { CartAdjustments, GroupAdjustments, ItemAdjustments } from '@app/cart/services/voucher-adjustments/data';
import { VoucherQualifiersService } from '@app/cart/services/voucher-qualifiers/voucher-qualifiers.service';
import { select, Store } from '@ngrx/store';
import * as fromCart from '@app/store/cart/cart.reducers';
import { Price } from '@app/model/catalogue-category/price.model';
import { AppState } from '@app/store/app.reducers';
import { applyAdjustmentOfVoucherQualifier } from '@app/cart/services/voucher-adjustments/adjustments';
import { remove } from '@app/utils/util-functions';

export interface ItemVoucherQualifiersMap {
    [itemId: number]: VoucherItemAdjustment[];
}

export interface VoucherQualifiersMap {
    [key: string]: ItemVoucherQualifiersMap;
}

export interface Savings {
    voucherReference: string;
    amount: number;
}

export interface AdjustmentsMap {
    [voucherId: string]: CartAdjustments;
}

/**
 * Keeps a record of qualifiers (qualifiers map) from a valid voucher reference
 * { [id]: ItemVoucherQualifiersMap }
 * id will be filled be cartId, groupId or itemId, these will correspond to modifier levels "ORDER", "SAMPLING", or
 * "ORDER_LINE".
 *
 * orderQualifiers is populated every time a valid voucher reference is added.
 * A corresponding cart adjustment is built after a set of voucher qualifiers is added.
 * This adjustment is added to the adjustments maps
 * { [id]: CartAdjustment }
 * Same with the qualifiers map, id will be filled be cartId, groupId or itemId. right now only cartId is implemented
 *
 * Use the cart adjustment to get the adjusted prices of items.
 */
@Injectable({
    providedIn: 'root'
})
export class VoucherAdjustmentsService {

    orderQualifiers$ = new BehaviorSubject<VoucherQualifiersMap>({});
    adjustments$ = new BehaviorSubject<AdjustmentsMap>({});

    constructor(
        private qualifierService: VoucherQualifiersService,
        private store: Store<AppState>
    ) {
    }

    public getCart(cartState$: Observable<fromCart.State>) {
        return cartState$.pipe(
            filter(Boolean),
            map(this.toCart),
            distinctUntilKeyChanged('id')
        );
    }

    public loadCartAdjustments(
        promoCode$: Observable<string>,
        groups$: Observable<CartGroup[]>,
        cartState$: Observable<fromCart.State>
    ): Observable<CartAdjustments> {
        const cart$ = this.getCart(cartState$);
        const cartId$ = this.getCartId();

        const cartOrderQualifiers$ = this.getCartGroupsWithAdjustments_GetCartOrderQualifiers(promoCode$, cartId$);
        return this.getCartGroupsWithAdjustments_GetAdjustments(cartOrderQualifiers$, cart$, groups$).pipe(
            tap((adjustment: CartAdjustments) => {
                const state = this.adjustments$.getValue();
                const update = { [adjustment.id]: adjustment };
                this.adjustments$.next(this.upsertAdjustments(state, update));
            }),
            shareReplay(1)
        );
    }

    public removeCartAdjustments(id: string | number) {
        let adjustments = this.adjustments$.getValue();
        adjustments = remove(adjustments, id);
        this.adjustments$.next(adjustments);
    }

    public getCartAdjustments(voucherId: string | number): Observable<CartAdjustments> {
        return this.adjustments$.pipe(
            map(adjustmentsMap => adjustmentsMap[voucherId]),
            map(adjustment => !adjustment ? new CartAdjustments(voucherId, 0, 0) : adjustment)
        );
    }

    public getCartWithAdjustments(
        cartState$: Observable<fromCart.State>,
        adjustments$: Observable<CartAdjustments>
    ): Observable<Cart> {
        const cart$ = this.getCart(cartState$);
        return combineLatest([cart$, adjustments$]).pipe(
            map(([cart, adjustments]) => this.applyCartAdjustments(cart, adjustments)),
            shareReplay(1)
        );
    }

    public getCartGroupsWithAdjustments(
        groups$: Observable<CartGroup[]>,
        adjustments$: Observable<CartAdjustments>
    ): Observable<CartGroup[]> {
        return combineLatest([groups$, adjustments$]).pipe(
            map(([groups, adjustments]) => this.applyGroupAdjustments(groups, adjustments)),
            shareReplay(1)
        );
    }

    public getCartSavings(
        adjustments$: Observable<CartAdjustments>,
        voucherReference$: Observable<string>): Observable<Savings> {
        return combineLatest([voucherReference$, adjustments$ ]).pipe(
            map(([voucherReference, adjustments]) => ({ voucherReference,  amount: -adjustments.adjustment })),
            startWith({ voucherReference: '', amount: 0 }),
            shareReplay(1)
        );
    }

    public getOrderAdjustments(
        itemQualifiers: ItemVoucherQualifiersMap,
        cart: Cart,
        groups: CartGroup[]): CartAdjustments {

        const cartAdjustments = this.toCartAdjustments(cart, groups);

        // set adjustments
        cartAdjustments.groups = cartAdjustments.groups.map(group => {
            const adjustmentIsValid = (adjustment: VoucherItemAdjustment) => adjustment.active && !adjustment.expired;

            const items = group.items.map((item) => {
                let adjustments: VoucherItemAdjustment[]  = itemQualifiers[item.id] || [];

                if (adjustments.length === 0) {
                    return item;
                }

                item.applicable = true;
                item.invalidExpired = !adjustments.some(adjustment => adjustmentIsValid(adjustment));

                // apply only valid adjustments
                adjustments = adjustments.filter(adjustment => adjustmentIsValid(adjustment));
                return applyAdjustmentOfVoucherQualifier(item, adjustments);
            });

            group.items = items;
            group.adjustment = items.reduce((result, item) => result + (item.adjustment * item.quantity), 0);
            return group;
        });

        cartAdjustments.adjustment =  cartAdjustments.groups.reduce((result, item) => result + item.adjustment, 0);
        return cartAdjustments;
    }

    public loadOrderQualifiers(promoCode: string, cartId: string) {
        return this.qualifierService.getOrderQualifiers(promoCode).pipe(
            switchMap((data: VoucherQualifier[]) => {
                let state = this.orderQualifiers$.getValue();
                state = this.upsertOrderQualifier(state, { [cartId]: this.toMap(data) });
                this.orderQualifiers$.next(state);
                return [state];
            }),
            catchError(e => [e]),
        );
    }

    public toCart(cart: fromCart.State): Cart {
        let price = new Price(0);
        if (cart.cartPrice !== null) {
            price = new Price(cart.cartPrice.amount);
        }
        return new Cart(cart.cartId, [], price);
    }

    private getCartId(): Observable<string> {
        return this.store.pipe(
            select('cart'),
            map(this.toCart),
            map((cart: Cart) => cart.id),
            distinctUntilChanged()
        );
    }

    private getCartGroupsWithAdjustments_GetCartOrderQualifiers(
        promoCode$: Observable<string>,
        cartId$: Observable<string>) {
        return promoCode$.pipe(
            withLatestFrom(cartId$),
            mergeMap(([code, cartId]) => this.loadOrderQualifiers(code, cartId).pipe(
                map(qualifiers => ({ qualifiers, cartId }))
            )),
            map(({ qualifiers, cartId }) => qualifiers[cartId] || {}),
        );
    }

    private getCartGroupsWithAdjustments_GetAdjustments(
        cartOrderQualifiers$: Observable<ItemVoucherQualifiersMap>,
        cart$: Observable<Cart>,
        groups$: Observable<CartGroup[]>) {
        return combineLatest([cartOrderQualifiers$, cart$, groups$]).pipe(
            map(([qualifiers, cart, groups]) => this.getOrderAdjustments(qualifiers, cart, groups)),
        );
    }

    // returns a map of voucher qualifiers with catalogue item id as key
    private toMap(data: VoucherQualifier[]): ItemVoucherQualifiersMap {
        return data.reduce((result, qualifier) => {
            const { modifierType, value } = qualifier;

            qualifier.voucherItems.map(voucherItem => {
                const { catalogueItemId, active, expired } = voucherItem;
                const existing: VoucherItemAdjustment[] = result[catalogueItemId] || [];
                const itemAdjustment: VoucherItemAdjustment = {
                    catalogueItemId,
                    active,
                    expired,
                    modifierType,
                    value
                };
                const itemQualifier = ({ [catalogueItemId]: [...existing, itemAdjustment] });
                result = { ...result, ...itemQualifier };
            });
            return result;
        },
        {});
    }

    private upsertOrderQualifier(state: VoucherQualifiersMap, update: VoucherQualifiersMap): VoucherQualifiersMap {
        return { ...state, ...update };
    }

    private upsertAdjustments(state: AdjustmentsMap, update: AdjustmentsMap): AdjustmentsMap {
        return { ...state, ...update };
    }

    private toCartAdjustments(cart: Cart, groups: CartGroup[]) {
        const cartAdjustments = new CartAdjustments(cart.id, cart.cartPrice.amount, 0);
        cartAdjustments.groups = groups.map(group => this.toGroupAdjustments(group));
        return cartAdjustments;
    }

    private toGroupAdjustments(group: CartGroup) {
        const groupAdjustments = new GroupAdjustments(group.id, group.groupPrice.amount, 0);
        groupAdjustments.items = group.items.map(item => this.toItemAdjustments(item));
        return groupAdjustments;
    }

    private toItemAdjustments(item: CartItem) {
        return new ItemAdjustments(item.item.id, item.item.price.amount, 0, item.quantity, false);
    }

    private applyGroupAdjustments(groups: CartGroup[], adjustments: CartAdjustments): CartGroup[] {

        return groups.map(group => {
            const clone = group.clone();
            const groupAdjustments  = adjustments.groups.find(g => g.id === group.id);

            if (!!groupAdjustments) {
                clone.items = clone.items.map((item: CartItem) => {
                    const cloneItem = item.clone();
                    const itemAdjustment = groupAdjustments.items.find(t => t.id === item.item.id);
                    if (!!itemAdjustment) {
                        const lineAdjustmentAmount = itemAdjustment.adjustment * cloneItem.quantity;
                        const price = new Price(cloneItem.quantityPrice.amount + lineAdjustmentAmount);
                        cloneItem.quantityPrice = price;
                    }
                    return cloneItem;
                });
            }

            const groupAdjustment = !groupAdjustments ? 0 : groupAdjustments.adjustment;
            const groupPrice = new Price(clone.groupPrice.amount + groupAdjustment);
            clone.groupPrice = groupPrice;
            return clone;
        });
    }

    private applyCartAdjustments(cart: Cart, adjustments: CartAdjustments): Cart {
        const price = new Price(cart.cartPrice.amount + adjustments.adjustment);
        return new Cart(cart.id, [], price);
    }

}
